import type { SVGProps } from 'react'

type P = SVGProps<SVGSVGElement>
const base = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5 } as const

export const Ic = {
  plan: (p: P) => <svg {...base} {...p}><rect x="3" y="5" width="18" height="16" rx="1.5"/><path d="M3 9h18M8 3v4M16 3v4"/></svg>,
  book: (p: P) => <svg {...base} {...p}><path d="M5 4h11a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3V4Z"/><path d="M5 17a3 3 0 0 1 3-3h11"/></svg>,
  cart: (p: P) => <svg {...base} {...p}><path d="M3 4h2l2.5 12h11l2-8H7"/><circle cx="9" cy="20" r="1.3"/><circle cx="17" cy="20" r="1.3"/></svg>,
  user: (p: P) => <svg {...base} {...p}><circle cx="12" cy="8" r="4"/><path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6"/></svg>,
  chat: (p: P) => <svg {...base} {...p}><path d="M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-7l-4 4v-4H6a2 2 0 0 1-2-2V6Z"/></svg>,
  search: (p: P) => <svg {...base} {...p}><circle cx="11" cy="11" r="6.5"/><path d="m20 20-3.5-3.5"/></svg>,
  plus: (p: P) => <svg {...base} {...p}><path d="M12 5v14M5 12h14"/></svg>,
  close: (p: P) => <svg {...base} {...p}><path d="m6 6 12 12M18 6 6 18"/></svg>,
  check: (p: P) => <svg {...base} strokeWidth={1.8} {...p}><path d="m5 12 4.5 4.5L19 7"/></svg>,
  chevL: (p: P) => <svg {...base} {...p}><path d="m14 6-6 6 6 6"/></svg>,
  chevR: (p: P) => <svg {...base} {...p}><path d="m10 6 6 6-6 6"/></svg>,
  chevD: (p: P) => <svg {...base} {...p}><path d="m6 9 6 6 6-6"/></svg>,
  clock: (p: P) => <svg {...base} {...p}><circle cx="12" cy="12" r="8"/><path d="M12 7v5l3 2"/></svg>,
  sparkle: (p: P) => <svg {...base} {...p}><path d="M12 4v6M12 14v6M4 12h6M14 12h6"/></svg>,
  more: (p: P) => <svg {...base} {...p}><circle cx="6" cy="12" r="1.2"/><circle cx="12" cy="12" r="1.2"/><circle cx="18" cy="12" r="1.2"/></svg>,
  filter: (p: P) => <svg {...base} {...p}><path d="M3 5h18M6 12h12M10 19h4"/></svg>,
  star: (p: P) => <svg {...base} {...p}><path d="m12 4 2.6 5.3 5.9.85-4.25 4.15 1 5.85L12 17.4l-5.25 2.75 1-5.85L3.5 10.15l5.9-.85L12 4Z"/></svg>,
  trash: (p: P) => <svg {...base} {...p}><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13"/></svg>,
  jar: (p: P) => <svg {...base} {...p}><path d="M8 3h8v3H8z"/><path d="M7 7h10l-1 13H8L7 7Z"/><path d="M9 12h6"/></svg>,
  image: (p: P) => <svg {...base} {...p}><rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="9" cy="10" r="1.6"/><path d="m4 18 5-5 4 4 3-3 4 4"/></svg>,
  camera: (p: P) => <svg {...base} {...p}><path d="M4 8h3l2-2h6l2 2h3v11H4z"/><circle cx="12" cy="13" r="3.5"/></svg>,
  grip: (p: P) => <svg {...base} {...p} fill="currentColor" stroke="none"><circle cx="9" cy="6" r="1.3"/><circle cx="15" cy="6" r="1.3"/><circle cx="9" cy="12" r="1.3"/><circle cx="15" cy="12" r="1.3"/><circle cx="9" cy="18" r="1.3"/><circle cx="15" cy="18" r="1.3"/></svg>,
}

export function ThumbSwatch({ hue, name, size = 36, radius = 4 }: { hue: number; name: string; size?: number; radius?: number }) {
  const bg = `oklch(0.78 0.10 ${hue})`
  const fg = `oklch(0.32 0.08 ${hue})`
  return (
    <div style={{
      width: size, height: size, borderRadius: radius, background: bg,
      display: 'grid', placeItems: 'center', flex: '0 0 auto',
      color: fg, fontFamily: "'Newsreader', Georgia, serif", fontSize: size * 0.5, lineHeight: 1,
      border: '1px solid rgba(34,25,18,0.10)',
    }}>{name[0]?.toUpperCase() ?? '?'}</div>
  )
}

// thumbVariantUrl rewrites a recipe-photo URL to request the small thumbnail
// variant the server serves at ?s=thumb (see backend getRecipeImage). Only
// touches same-origin /api photo URLs — a blob:/data: preview during upload is
// passed through untouched. The URL always already carries a ?v= cache-buster,
// so appending &s=thumb is safe.
function thumbVariantUrl(url: string): string {
  return url.startsWith('/api/') ? url + '&s=thumb' : url
}

// RecipeThumb renders a recipe's custom photo when it has one, falling back to
// the hue-derived ThumbSwatch placeholder. Same footprint (size/radius) either
// way so every call site stays visually consistent. It always loads the small
// thumbnail variant — the full image is only fetched on tap-to-expand.
export function RecipeThumb({ imageUrl, hue, name, size = 36, radius = 4 }: { imageUrl?: string; hue: number; name: string; size?: number; radius?: number }) {
  if (imageUrl) {
    return (
      <img src={thumbVariantUrl(imageUrl)} alt="" width={size} height={size} style={{
        width: size, height: size, borderRadius: radius, objectFit: 'cover',
        flex: '0 0 auto', display: 'block', border: '1px solid rgba(34,25,18,0.10)',
      }} />
    )
  }
  return <ThumbSwatch hue={hue} name={name} size={size} radius={radius} />
}
